import type { RequestOptions } from "@@/plugin-request/request";
import { history, request, type RequestConfig } from "@umijs/max";
import { message } from "antd";
import { errorCode } from "./constants/error-code";

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  error_code?: number;
  error?: string;
  showType?: ErrorShowType;
  total_record?: number;
  length?: number;
}

// Helper: build Authorization header with stored token_type (default Bearer)
const getAuthHeader = (token: string | null | undefined) => {
  if (!token) return undefined as string | undefined;
  const storedType = (localStorage.getItem("token_type") || "Bearer").toLowerCase();
  const scheme = storedType === "basic" ? "Basic" : "Bearer";
  return `${scheme} ${token}`;
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */

// Refresh token function
const refreshToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem("rid");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await fetch(
      `${process.env.UMI_API_BASE_URL}/${process.env.UMI_API_OPTION_URL}/v1/sso/sso_fpt_refresh_token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      }
    );

    const result = await response.json();

    if (result?.success && result?.data?.access_token) {
      const newAccessToken = result.data.access_token as string;
      const newRefreshToken = result.data.refresh_token as string | undefined;

      localStorage.setItem("access_token", newAccessToken);
      // Refresh flow uses Bearer
      localStorage.setItem("token_type", "Bearer");
      if (newRefreshToken) {
        localStorage.setItem("rid", newRefreshToken);
      }

      return newAccessToken;
    } else {
      throw new Error("Failed to refresh token");
    }
  } catch (error) {
    console.error("Token refresh failed:", error);
    localStorage.removeItem("access_token");
    localStorage.removeItem("rid");
    throw error;
  }
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason: any) => void;
}> = [];

// Process failed requests after token refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};
export const errorConfig: RequestConfig = {
  timeout: 60000,
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    errorHandler: async (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      const { response, config } = error;

      if (response?.status === 401) {
        const originalRequest = config;

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              const auth = getAuthHeader(token as string | null | undefined);
              if (auth && originalRequest.headers) {
                originalRequest.headers.Authorization = auth as any;
              }
              return request(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          processQueue(null, newToken);

          // Retry the original request with new token
          const auth = getAuthHeader(newToken);
          if (auth && originalRequest.headers) {
            originalRequest.headers.Authorization = auth as any;
          }
          return request(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);

          message.error("Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại");
          // Redirect to login page
          if (window.location.pathname !== "/user/login") {
            history.push("/user/login");
          }

          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      // Handle other error cases
      if (response) {
        const { status, statusText, data } = response;

        switch (status) {
          case 403:
            message.error("Bạn không có quyền truy cập tài nguyên này");
            break;

          case 404:
            message.error("Không tìm thấy tài nguyên");
            break;

          case 422:
            // Handle validation errors
            if (data?.errors) {
              Object.values(data.errors).forEach((errorMsg: any) => {
                message.error(Array.isArray(errorMsg) ? errorMsg[0] : errorMsg);
              });
            } else {
              message.error(data?.message || "Dữ liệu không hợp lệ");
            }
            break;

          case 500:
            message.error("Lỗi máy chủ nội bộ");
            break;

          default:
            message.error(
              errorCode[data?.error_code as number] || `Error ${status}: ${data?.error}` || "Có lỗi xảy ra"
            );
        }
      } else if (error.name === "AbortError") {
        // Request was cancelled
        console.log("Request cancelled");
      } else {
        // Network error or other errors
        message.error("Lỗi kết nối mạng, vui lòng kiểm tra kết nối");
      }

      throw error;
    },

    errorThrower: (res: any) => {
      const { success, data, errorCode: errCode, errorMessage, showType } = res;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = "BizError";
        error.info = { errorCode: errCode, errorMessage, showType, data };
        throw error;
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const token = localStorage.getItem("access_token");
      if (token && config.headers) {
        const auth = getAuthHeader(token);
        if (auth) {
          (config.headers as any).Authorization = auth;
        }
      }

      // Add default headers
      if (!config.headers?.["Content-Type"]) {
        config.headers = {
          ...config.headers,
          "Content-Type": "application/json",
        } as any;
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response: any) => {
      // Log mọi response từ backend (để kiểm tra call API thừa)
      const url = response?.config?.url ?? response?.url ?? "?";
      console.log("[API] response", { url, response });
      // 拦截响应数据，进行个性化处理
      const { data, success, error_code, error, total_record, length } =
        response as unknown as ResponseStructure;

      if (success === false) {
        // Check if error_code exists in errorCode mapping
        const map = errorCode as Record<number, string>;
        if (error_code && map[error_code]) {
          message.error(map[error_code]);
        } else if (error) {
          message.error(error);
        } else {
          message.error("Lỗi hệ thống");
        }
      }
      return response;
    },
  ],
};
